import { useCallback, useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { acceptApproval } from '../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { Services } from '../../store/slices/timesheet/timesheetPageSlice'
import { AppDispatch } from '../../store/store'
import { BonificationDiverse, CompanyGratificationParam, Refund } from '../../types/gratifications'
import {
	useGetCompanyGratificationsQuery,
	useGetCompanyGratificationsWithRulesQuery,
} from '../../store/api/timesheet/timesheetCompanyGratificationApi'
import { verificationApproval } from '../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsVerificationSlice'
import { hasChanges } from '../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import { devLog } from '../feedback'
import { useMemo } from 'react'
import { TimesheetStoreState } from '../../store/timesheetStore'
import { useIsTimesheetApproved } from './approvedTimesheet'
import { TimesheetStatusEnum } from '../../store/slices/timesheet/timesheetDetailSlice'

interface GratificationsState {
	gratificationParams: CompanyGratificationParam[]
	refundsToShow: CompanyGratificationParam[]
	showRefundField: boolean
	blockedGratifications: string[]
	refundsWithRules: BonificationDiverse[]
}

interface GratificationsActions {
	handleGratificationValue: (value: number, grat: CompanyGratificationParam) => void
	handleAddGratification: (gratification: CompanyGratificationParam) => void
	handleRemoveGratification: (id: number) => void
	// @ts-expect-error - values type depends on the input component used, can be adapted as needed
	handleLegacyRefundChange: (values) => void
	parseRefunds: (refundsString: string | null) => Refund[]
}

interface RuleApplicationResult {
	updatedParams: CompanyGratificationParam[]
	blockedDescriptions: string[]
	totalRefundValue: number
}

interface RuleApplication {
	applyRules: (
		refundsWithRules: BonificationDiverse[],
		gratificationParams: CompanyGratificationParam[],
	) => RuleApplicationResult
}

/**
 * Custom hook to manage company gratification rules and parameters.
 * This hook is now PURE - it doesn't cause side effects, just calculates results.
 */
export const useGratificationRules = (): RuleApplication => {
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	const blockGratification = useCallback(
		(
			fieldName: string,
			description: string,
			params: CompanyGratificationParam[],
			blockedDescriptions: string[],
		): {
			updatedParams: CompanyGratificationParam[]
			updatedBlocked: string[]
		} => {
			const updatedParams = params.map(param => (param.campoSP === fieldName ? { ...param, valor: 0 } : param))
			const updatedBlocked = [...blockedDescriptions, description]
			return { updatedParams, updatedBlocked }
		},
		[],
	)

	const applyMealRules = useCallback(
		(
			rule: BonificationDiverse,
			service: Services,
			params: CompanyGratificationParam[],
			blockedDescriptions: string[],
		): {
			updatedParams: CompanyGratificationParam[]
			updatedBlocked: string[]
		} => {
			const serviceDate = service.service.startDateTime?.split('T')[0]
			const ruleDate = rule.dataServico.split('T')[0]

			if (serviceDate !== ruleDate) {
				return { updatedParams: params, updatedBlocked: blockedDescriptions }
			}

			let currentParams = params
			let currentBlocked = blockedDescriptions

			if (rule.inibeAlmoco) {
				const result = blockGratification('@AlugueresAlmoco', 'Almoço', currentParams, currentBlocked)
				currentParams = result.updatedParams
				currentBlocked = result.updatedBlocked
			}

			if (rule.inibeJantar) {
				const result = blockGratification('@AlugueresJantar', 'Jantar', currentParams, currentBlocked)
				currentParams = result.updatedParams
				currentBlocked = result.updatedBlocked
			}

			if (rule.inibeDormida) {
				const result = blockGratification('@AlugueresDormida', 'Dormida', currentParams, currentBlocked)
				currentParams = result.updatedParams
				currentBlocked = result.updatedBlocked
			}

			return { updatedParams: currentParams, updatedBlocked: currentBlocked }
		},
		[blockGratification],
	)

	const applyAgencyGratifications = useCallback(
		(refundsWithRules: BonificationDiverse[], params: CompanyGratificationParam[]): CompanyGratificationParam[] => {
			const agencyGratificationTotal = refundsWithRules.reduce((total, rule) => total + rule.gratificacaoAgencias, 0)

			if (agencyGratificationTotal > 0) {
				return params.map(param =>
					param.campoSP === '@AlugueresOutras' ? { ...param, valor: agencyGratificationTotal } : param,
				)
			}

			return params
		},
		[],
	)

	const applyRules = useCallback(
		(
			refundsWithRules: BonificationDiverse[],
			gratificationParams: CompanyGratificationParam[],
		): RuleApplicationResult => {
			let totalBlockedValue = 0
			let currentParams = [...gratificationParams]
			let blockedDescriptions: string[] = []

			const validRules = refundsWithRules.filter(rule => !rule.naoExisteServico)

			if (validRules.length !== refundsWithRules.length) {
				devLog('There are rules with no associated service.')
				devLog(
					'Rules with no service:',
					refundsWithRules.filter(rule => rule.naoExisteServico),
				)
			}

			timesheetPage.services.forEach((service: Services) => {
				validRules.forEach(rule => {
					totalBlockedValue += rule.gratificacao
					const result = applyMealRules(rule, service, currentParams, blockedDescriptions)
					currentParams = result.updatedParams
					blockedDescriptions = result.updatedBlocked
				})
			})

			currentParams = applyAgencyGratifications(validRules, currentParams)

			return {
				updatedParams: currentParams,
				blockedDescriptions,
				totalRefundValue: totalBlockedValue,
			}
		},
		[timesheetPage.services, applyMealRules, applyAgencyGratifications],
	)

	return { applyRules }
}

/**
 * Custom hook to define company gratification rules and parameters.
 */
export const useGratifications = (): GratificationsState &
	GratificationsActions & {
		isLoading: boolean
		isError: boolean
		onRetry: () => void
	} => {
	const dispatch = useDispatch<AppDispatch>()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)
	const timesheetApprovalDetailsVerification = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsVerification,
	)
	const timesheetHasChangesFromReviewer = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalHasChanges.hasChangesFromReviewer,
	)

	// API queries
	const driverId = useMemo(() => timesheetPage.driver.driverId ?? 0, [timesheetPage.driver.driverId])
	const {
		data: companyGratificationParams,
		isFetching: isLoading,
		isError,
		refetch: onRetry,
	} = useGetCompanyGratificationsQuery(driverId, {
		skip: isNaN(driverId) || driverId <= 0,
	})

	const timesheetId = useMemo(() => timesheetPage.timeSheetId ?? 0, [timesheetPage.timeSheetId])
	const isTimeSheetApproved = useIsTimesheetApproved()

	// Rules only apply for Submitted or ReadyForEdit status
	const shouldSkip = useMemo(() => {
		return (
			isNaN(timesheetId) ||
			timesheetId <= 0 ||
			(timesheetPage.status !== TimesheetStatusEnum.Submitted &&
			timesheetPage.status !== TimesheetStatusEnum.AutoSubmitted &&
			timesheetPage.status !== 'ReadyForEdit')
		)
	}, [timesheetId, timesheetPage.status])

	const { data: gratificationsWithRulesResponse, isFetching: isLoadingWithRules } =
		useGetCompanyGratificationsWithRulesQuery(timesheetId, {
			skip: shouldSkip,
		})

	// Local state
	const [gratificationParams, setGratificationParams] = useState<CompanyGratificationParam[]>([])
	const [refundsToShow, setRefundsToShow] = useState<CompanyGratificationParam[]>([])
	const [showRefundField, setShowRefundField] = useState<boolean>(false)
	const [savedRefunds, setSavedRefunds] = useState<string>('')
	const [refundsWithRules, setRefundsWithRules] = useState<BonificationDiverse[]>([])
	const [blockedGratifications, setBlockedGratifications] = useState<string[]>([])

	const rulesAppliedRef = useRef<boolean>(false)

	// Parse refunds utility
	const parseRefunds = useCallback((refundsString: string | null): Refund[] => {
		if (!refundsString) return []
		try {
			return JSON.parse(refundsString)
		} catch {
			return []
		}
	}, [])

	// Get gratification rules applier
	const { applyRules } = useGratificationRules()

	// Gratifications to display: only those with value > 0
	const toVisible = useCallback(
		(params: CompanyGratificationParam[]): CompanyGratificationParam[] =>
			params.filter(p => p.valor > 0),
		[],
	)

	// Apply rules automatically when they arrive (only once, only for Submitted/ReadyForEdit)
	useEffect(() => {
		if (refundsWithRules.length > 0 && gratificationParams.length > 0 && !rulesAppliedRef.current) {
			const validRules = refundsWithRules.filter(rule => !rule.naoExisteServico)
			const ruleResult = applyRules(validRules, gratificationParams)

			const hasParamChanges = JSON.stringify(gratificationParams) !== JSON.stringify(ruleResult.updatedParams)

			if (hasParamChanges) {
				setGratificationParams(ruleResult.updatedParams)
				setBlockedGratifications(ruleResult.blockedDescriptions)
				setRefundsToShow(toVisible(ruleResult.updatedParams))

				const finalRefunds: Refund[] = ruleResult.updatedParams.map(param => ({
					value: param.valor,
					spField: param.campoSP,
					id: param.id,
				}))
				const newRefundsString = JSON.stringify(finalRefunds)

				if (
					timesheetApprovalDetailsAccept.refunds !== newRefundsString ||
					timesheetApprovalDetailsAccept.refund !== ruleResult.totalRefundValue
				) {
					dispatch(
						acceptApproval({
							refunds: newRefundsString,
							refund: ruleResult.totalRefundValue,
						}),
					)
				}
			}
			rulesAppliedRef.current = true
		}
	}, [
		refundsWithRules,
		applyRules,
		dispatch,
		timesheetApprovalDetailsAccept.refunds,
		timesheetApprovalDetailsAccept.refund,
		gratificationParams.length,
		toVisible,
	])

	// Initialize savedRefunds from the correct source depending on approval state
	useEffect(() => {
		if (savedRefunds !== '') return
		if (isTimeSheetApproved) {
			// timesheetApprovalDetailsAccept.refunds is cleared by the cleanup effect on unmount,
			// so fall back to timesheetPage.approval?.refunds after a reload.
			const source = timesheetApprovalDetailsAccept.refunds ?? timesheetPage.approval?.refunds ?? null
			if (source) {
				setSavedRefunds(source)
			}
		} else {
			if (timesheetPage.refunds) {
				setSavedRefunds(timesheetPage.refunds)
			}
		}
	}, [isTimeSheetApproved, timesheetApprovalDetailsAccept.refunds, timesheetPage.refunds, timesheetPage.approval?.refunds, savedRefunds])

	// Handle refund field visibility
	useEffect(() => {
		if (timesheetHasChangesFromReviewer) return
		setShowRefundField(timesheetApprovalDetailsAccept.refund > 0)
	}, [timesheetApprovalDetailsAccept.refund, timesheetHasChangesFromReviewer])

	// Update refunds with rules
	useEffect(() => {
		if (gratificationsWithRulesResponse) {
			setRefundsWithRules(gratificationsWithRulesResponse)
		}
	}, [gratificationsWithRulesResponse])

	// Initialization: combine API params with saved refund values
	useEffect(() => {
		if (!companyGratificationParams || isError || isLoading || gratificationParams.length > 0) return

		// Guard: when companyGratificationParams is cached (no loading delay), this effect and the
		// savedRefunds sync effect run in the same render cycle. Without this, savedRefunds is still
		// '' here even though timesheetPage.refunds already has the correct value, causing all values
		// to reset to 0. Wait for the next render where savedRefunds will have been set.
		const pendingSource = isTimeSheetApproved
			? (timesheetApprovalDetailsAccept.refunds ?? timesheetPage.approval?.refunds)
			: timesheetPage.refunds
		if (savedRefunds === '' && pendingSource) return

		const refunds = parseRefunds(savedRefunds)
		const refundsMap = new Map(refunds.map(refund => [refund.spField, refund.value]))

		const initialParams = companyGratificationParams.map((param: CompanyGratificationParam) => ({
			...param,
			valor: refundsMap.get(param.campoSP) ?? 0,
		}))

		setGratificationParams(initialParams)
		setRefundsToShow(toVisible(initialParams))
		rulesAppliedRef.current = false

		// Repopulate Redux after remount — the cleanup effect wipes refunds to null,
		// so the next save would send null unless we restore it here.
		const initialRefunds: Refund[] = initialParams.map(param => ({
			value: param.valor,
			spField: param.campoSP,
			id: param.id,
		}))
		dispatch(
			acceptApproval({
				refunds: JSON.stringify(initialRefunds),
				refund: initialRefunds.reduce((total, r) => total + r.value, 0),
			}),
		)
	}, [
		companyGratificationParams,
		isError,
		isLoading,
		savedRefunds,
		parseRefunds,
		gratificationParams.length,
		toVisible,
		isTimeSheetApproved,
		timesheetApprovalDetailsAccept.refunds,
		timesheetPage.refunds,
		timesheetPage.approval?.refunds,
		dispatch,
	])

	// Validation effect
	useEffect(() => {
		const gratificationError =
			timesheetApprovalDetailsAccept.refund > 100 || gratificationParams.some(param => param.valor > param.limite)

		if (timesheetApprovalDetailsVerification.verification.gratificationError !== gratificationError) {
			dispatch(
				verificationApproval({
					verification: {
						...timesheetApprovalDetailsVerification.verification,
						gratificationError,
					},
				}),
			)
		}
	}, [
		gratificationParams,
		timesheetApprovalDetailsAccept.refund,
		dispatch,
		timesheetApprovalDetailsVerification.verification.gratificationError,
	])

	// Cleanup effect
	useEffect(() => {
		return () => {
			dispatch(acceptApproval({ refunds: null }))
		}
	}, [])

	const handleGratificationValue = useCallback(
		(value: number, grat: CompanyGratificationParam) => {
			dispatch(hasChanges({ hasChangesFromReviewer: true }))

			const newValue = isNaN(value) ? 0 : value

			const updatedParams = gratificationParams.map(param =>
				param.campoSP === grat.campoSP ? { ...param, valor: newValue } : param,
			)

			setGratificationParams(updatedParams)
			setRefundsToShow(prev => prev.map(r => (r.campoSP === grat.campoSP ? { ...r, valor: newValue } : r)))

			const refunds: Refund[] = updatedParams.map(param => ({
				value: param.valor,
				spField: param.campoSP,
				id: param.id,
			}))

			dispatch(
				acceptApproval({
					refunds: JSON.stringify(refunds),
					refund: refunds.reduce((total, r) => total + r.value, 0),
				}),
			)
		},
		[dispatch, gratificationParams],
	)

	const handleAddGratification = useCallback((gratification: CompanyGratificationParam) => {
		setRefundsToShow(prev => [...prev, gratification])
	}, [])

	const handleRemoveGratification = useCallback(
		(id: number) => {
			const grat = refundsToShow.find(g => g.id === id)
			setRefundsToShow(prev => prev.filter(g => g.id !== id))
			if (grat) {
				handleGratificationValue(0, grat)
			}
		},
		[refundsToShow, handleGratificationValue],
	)

	const handleLegacyRefundChange = useCallback(
		// @ts-expect-error - values type depends on the input component used, can be adapted as needed
		values => {
			dispatch(hasChanges({ hasChangesFromReviewer: true }))
			const { floatValue } = values
			dispatch(
				acceptApproval({
					...timesheetApprovalDetailsAccept,
					refund: floatValue ?? 0,
				}),
			)
		},
		[dispatch, timesheetApprovalDetailsAccept],
	)

	return {
		gratificationParams,
		refundsToShow,
		showRefundField,
		blockedGratifications,
		refundsWithRules,
		handleGratificationValue,
		handleAddGratification,
		handleRemoveGratification,
		handleLegacyRefundChange,
		parseRefunds,
		isLoading: isLoading || isLoadingWithRules,
		isError,
		onRetry,
	}
}
